import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Bell, X, CheckCheck, Trash2, ExternalLink, Inbox } from 'lucide-react';
import { notificationLink } from '../lib/notificationLinks';
import { handleNotificationOpen, isNotificationUnread } from '../lib/notificationOpen';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NotificationBell() {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const fetchNotifications = async () => {
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/notifications?limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    const existing = notifications.find((n) => n.id === notificationId);
    try {
      await fetch(`${API_URL}/api/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
      );
      if (existing && isNotificationUnread(existing)) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${API_URL}/api/notifications/read-all`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const deleteNotification = async (notificationId, e) => {
    e.stopPropagation();
    const existing = notifications.find((n) => n.id === notificationId);
    try {
      await fetch(`${API_URL}/api/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      if (existing && isNotificationUnread(existing)) setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const onNotificationOpen = (notification) => {
    handleNotificationOpen({
      notification,
      user,
      navigate,
      markAsRead,
      closeDropdown: () => setIsOpen(false),
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user, token]);

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (Number.isNaN(seconds)) return '';
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full hover:bg-slate-100 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-slate-700" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs text-white flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white border border-slate-200 rounded-lg shadow-xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
            <h3 className="font-semibold text-slate-800">{t('notification.panel_title', 'Notifications')}</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-signal-blue hover:text-signal-blue-hover flex items-center gap-1"
                >
                  <CheckCheck className="w-3 h-3" />
                  {t('notification.mark_all_read', 'Mark all read')}
                </button>
              )}
              <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-slate-200 rounded">
                <X className="w-4 h-4 text-slate-600" />
              </button>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-slate-500">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>{t('notification.empty', 'No notifications yet')}</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const unread = isNotificationUnread(notif);
                const link = notificationLink(notif, user);
                const openLabel = link
                  ? t('notification.open_item', 'Open notification')
                  : unread
                    ? t('notification.mark_read', 'Mark as read')
                    : undefined;

                return (
                  <div
                    key={notif.id}
                    role={link ? 'button' : undefined}
                    tabIndex={link ? 0 : undefined}
                    aria-label={openLabel}
                    title={openLabel}
                    data-testid={`notification-item-${notif.id}`}
                    className={`px-4 py-3 border-b border-slate-100 transition-colors ${
                      link ? 'cursor-pointer hover:bg-slate-100' : unread ? 'cursor-pointer hover:bg-slate-50' : ''
                    } ${unread ? 'bg-blue-50/50' : 'bg-white'}`}
                    onClick={() => onNotificationOpen(notif)}
                    onKeyDown={(e) => {
                      if (link && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        onNotificationOpen(notif);
                      }
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${unread ? 'bg-signal-blue' : 'bg-transparent'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm font-medium ${unread ? 'text-slate-800' : 'text-slate-600'}`}>
                            {notif.title}
                          </p>
                          <span className="text-xs text-slate-500 whitespace-nowrap">
                            {formatTimeAgo(notif.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mt-0.5 line-clamp-2">{notif.message}</p>

                        <div className="flex items-center gap-3 mt-2">
                          {link && (
                            <Link
                              to={link}
                              onClick={(e) => {
                                e.stopPropagation();
                                onNotificationOpen(notif);
                              }}
                              className="text-xs text-signal-blue hover:underline flex items-center gap-1"
                            >
                              {t('common.view', 'View')} <ExternalLink className="w-3 h-3" />
                            </Link>
                          )}
                          <button
                            type="button"
                            onClick={(e) => deleteNotification(notif.id, e)}
                            className="text-xs text-slate-500 hover:text-red-500 flex items-center gap-1"
                            aria-label={t('notification.delete', 'Delete notification')}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <Link
            to="/notifications"
            onClick={() => setIsOpen(false)}
            className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-signal-blue hover:bg-slate-50 border-t border-slate-200"
          >
            <Inbox className="w-4 h-4" />
            {t('notification.open_inbox', 'Open full inbox')}
          </Link>
        </div>
      )}
    </div>
  );
}
